import * as React from "react";
import { ResponsiveMode, Dropdown, IDropdownOption, TooltipHost, IDropdownStyles, Stack, IStackTokens, PanelType, Label, Spinner, SpinnerSize, FontIcon, IRenderFunction, TooltipOverflowMode } from "@fluentui/react";
import { LanguageContext } from "../../../../../../../utilities/LocalizationModule";
import { INameValuePairIndex } from "../../../../../interfaces/INameValuePairIndex";
import { DatasourceService } from "../../../../../services/DatasourceService";
import { DataSourceDropdownInitializer } from "../../../../helpers/datasourceDropdownInitializer";
import { DatasourceHelper } from "../../../../helpers/dataSourceHelper";
import { DataSourcePanel } from "../../../../inputs/datasourcePanel";
import { IBaseInputProps } from "../../../../../interfaces/advancedSearch/IBaseInputProps";
import advancedSearchStateManager from "../../../../../../../advancedSearch/advancedSearchStateManager";
import { logger } from "../../../../../../../utilities/logger";

const dropdownStyles: Partial<IDropdownStyles> = {
  dropdown: { maxWidth: 301 },
  dropdownOptionText: { overflow: 'visible', whiteSpace: 'normal' },
  dropdownItem: { height: 'auto' },
};

const onRenderTitle = (options: IDropdownOption[], defaultRender?: IRenderFunction<IDropdownOption[]>) => {
  const option: IDropdownOption = Array.isArray(options) && options.length > 0 && options[0];
  return <TooltipHost content={option?.text} overflowMode={TooltipOverflowMode.Parent}>
    {defaultRender(options)}
  </TooltipHost>
}

const DatasourceDropdownInput: React.FunctionComponent<IBaseInputProps> = props => {
  const languageStrings = React.useContext(LanguageContext);
  const [isOpen, setIsOpen] = React.useState(false);
  const [datasources, setDatasources] = React.useState<IDropdownOption[]>([]);
  const [searchInputs, setSearchInputs] = React.useState<INameValuePairIndex[]>([]);
  const [boundedField, setBoundedField] = React.useState<string>("");
  const dataSourceService = new DatasourceService();
  const dataSourceHelper = new DatasourceHelper(dataSourceService);
  const [dataSourceItem, setDataSource] = React.useState(
    DatasourceHelper.getInputDataSource(advancedSearchStateManager.dataSourceItems, props.displayName, props.selectedSites, props.selectedLibraries)
  );
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  React.useEffect(() => {
    const setupAsyncWrapper = async () => {
      setIsLoading(true);
      try {
        const dropdownInitializer = new DataSourceDropdownInitializer(
          dataSourceService,
          dataSourceItem,
          props.displayName,
          false
        );
        const [result] = await Promise.all([dataSourceHelper.getSearchStructure(dataSourceItem), dropdownInitializer.initialize(setDatasources)]);
        if (Array.isArray(result)) setSearchInputs(result.map((item, index) => ({ name: item, value: null, index: index })));
        const boundedFieldName = DatasourceHelper.getBoundedField(dataSourceItem, props.displayName);
        setBoundedField(boundedFieldName ?? "");
      } catch (error) {
        logger(error, "datasourceDropDownInput.tsx (useEffect)");
      }
      setIsLoading(false);
    };

    setupAsyncWrapper();
  }, []);

  const closePanel = () => {
    advancedSearchStateManager.isDataSourcePanelActive = false;
    setIsOpen(false);
  };

  const updateInputValue = (newValue: string) => {
    props.handleValueChange(props.searchInputData?.index, [newValue]);
    updateSearch(newValue);
  };

  const updateSearchInputs = (index: number, value: string) => {
    DatasourceHelper.updateSearchInputs(index, value, searchInputs, setSearchInputs);
  };

  const openPanel = async () => {
    advancedSearchStateManager.isDataSourcePanelActive = true;
    updateSearch(props.searchInputData ? props.searchInputData?.value : "");
    setIsOpen(true);
  };

  const updateSearch = (newValue: string) => {
    const index = searchInputs.findIndex(item => item.name?.trim().toLowerCase() === boundedField?.trim().toLowerCase());
    updateSearchInputs(index, newValue);
  };

  const onChangeHandle = (event: React.FormEvent<HTMLDivElement>, item: IDropdownOption): void => {
    props.handleValueChange(props.searchInputData?.index, item.key);
    updateSearch(item.key as string);
  };

  const input = (
    <Dropdown
      label={props.displayName}
      selectedKey={props.searchInputData ? props.searchInputData?.value : undefined}
      placeholder={languageStrings.SelectOption}
      onChange={onChangeHandle}
      onRenderTitle={onRenderTitle}
      options={datasources}
      styles={dropdownStyles}
      className="xb-sp-input"
    // responsiveMode={ResponsiveMode.small}
    />
  );

  const stackTokens: IStackTokens = {
    childrenGap: 5
  };

  return (
    <>
      <Stack horizontal verticalAlign="start" tokens={stackTokens}>
        <Stack.Item grow={1}>{input}</Stack.Item>
        <Stack.Item>
          {isLoading
            ? <Spinner size={SpinnerSize.small} style={{ paddingTop: 36, marginLeft: -2 }} />
            : <div className="xb-search-icon-container">
              <TooltipHost content={`${languageStrings.Search} ${dataSourceItem?.name} ${languageStrings.Datasource}`}>
                <FontIcon iconName="Search" onClick={openPanel} />
              </TooltipHost>
            </div>}
        </Stack.Item>
      </Stack>
      <DataSourcePanel
        type={PanelType.customNear}
        displayName={dataSourceItem?.name}
        closePanel={closePanel}
        isOpen={isOpen}
        datasourceItem={dataSourceItem}
        datasourceService={dataSourceService}
        searchInputs={searchInputs}
        updateSearchInputs={updateSearchInputs}
        updateInputValue={updateInputValue}
        boundedField={boundedField}
      />
    </>
  );
};

export default DatasourceDropdownInput;